import { Dispatch, SetStateAction } from "react";
import { configProps, Dropdowns, ViewNames } from "../types";
import VisibleColumnsDropdown from "./VisibleColumnsDropdown";
import FiltersDropdowns from "./FiltersDropdowns";
import { TableTitle } from "./TableTitle";
import SearchBar from "./SearchBar";
import DateFilter from "./DateFilter";
import { useErrorMarking } from "../contexts/ErrorMarkingContext";
import { SelectButton, SelectButtonChangeEvent } from "primereact/selectbutton";
import EditModeSwitch from "./EditModeSwitch";
import { Dropdown } from "primereact/dropdown";
import { getIsNationalFromUrl, isNationalOptions } from "../helpers/isNational";
import { useEditMode } from "../contexts/EditModeContext";
import { useSearchParams } from "react-router";
import { InputSwitch } from "primereact/inputswitch";
import { useUrlState } from "../hooks/useUrlState";
import { useAppDispatch } from "../redux/store";
import { Button } from "primereact/button";
import { createFormSlice } from "../redux/slices/createForm";

type TableTitleRowProps<
  TDropdowns extends Partial<Dropdowns>,
  TView extends ViewNames,
> = {
  visibleColumns: configProps<TView>["columns"];
  columns: configProps<TView>["columns"];
  setVisibleColumns: Dispatch<SetStateAction<configProps<TView>["columns"]>>;
  globalFilterValue: string;
  setGlobalFilterValue: Dispatch<SetStateAction<string>>;
  config: configProps<TView>;
  dropdownConfigs: TDropdowns;
};

export default function TableTitleRow<
  TDropdowns extends Partial<Dropdowns>,
  TView extends ViewNames,
>({
  visibleColumns,
  columns,
  setVisibleColumns,
  globalFilterValue,
  setGlobalFilterValue,
  config,
  dropdownConfigs,
}: TableTitleRowProps<TDropdowns, TView>) {
  const { errorMarkingScope, setErrorMarkingScope } = useErrorMarking();
  const { isEditMode } = useEditMode();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showCompletedRows, setShowCompletedRows } = useUrlState();
  const dispatch = useAppDispatch();

  const firstRow = (
    <div className="toolbar-first-row">
      <TableTitle title={config.title} />
      <div className="toolbar-row--inner-container">
        <EditModeSwitch />
        <div className="toggle-completed flex-container">
          <InputSwitch
            id="show-completed"
            checked={showCompletedRows}
            onChange={(e) => setShowCompletedRows(e.value)}
          />
          <label htmlFor="show-completed">Pokaż zakończone</label>
        </div>
        <div className="toolbar-row--inner-container__buttons">
          <Button
            label="Dodaj nowy"
            icon="pi pi-plus"
            iconPos="right"
            onClick={() => {
              dispatch(
                createFormSlice.actions.openForm({
                  mode: "create",
                }),
              );
            }}
          />
          <SelectButton
            className="error-marking-buttons"
            value={errorMarkingScope}
            allowEmpty
            optionLabel="label"
            optionValue="value"
            disabled={isEditMode}
            options={[
              { label: "Komórka", value: "cell" },
              { label: "Wiersz", value: "row" },
            ]}
            onChange={(e: SelectButtonChangeEvent) => {
              setErrorMarkingScope(e.value as typeof errorMarkingScope);
            }}
          />
        </div>
      </div>
    </div>
  );

  const secondRow = (
    <div className="toolbar-second-row">
      <div className="toolbar-row--inner-container">
        <SearchBar
          globalFilterValue={globalFilterValue}
          setGlobalFilterValue={setGlobalFilterValue}
        />

        <DateFilter config={config} />

        <FiltersDropdowns
          columns={config.columns}
          dropdownConfigs={dropdownConfigs}
        />

        {/* Check if 'unloadingLocation' is in columns to conditionally render the isNational filter */}
        {columns.some((column) => column.field === "unloadingLocation") && (
          <Dropdown
            value={getIsNationalFromUrl(searchParams)}
            options={isNationalOptions}
            optionLabel="label"
            optionValue="value"
            onChange={(e) =>
              setSearchParams((prev) => {
                const params = new URLSearchParams(prev);

                if (e.value !== null && e.value !== undefined) {
                  params.set("isNational", String(e.value));
                } else {
                  params.delete("isNational");
                }

                return params;
              })
            }
            placeholder="Rodzaj dostawy"
            showClear
          />
        )}
      </div>
      <VisibleColumnsDropdown<TView>
        allColumns={config.columns}
        visibleColumns={visibleColumns}
        setVisibleColumns={setVisibleColumns}
      />
    </div>
  );

  return (
    <div className="toolbar">
      {firstRow}
      {secondRow}
    </div>
  );
}
